import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Box, Button, Divider, Stack } from "@mui/material";

import { COLORS, CustomMaterialTable, Label14Medium } from "components";

import {
  useAmplitude,
  useGetConfigParameter,
  useNotify,
  useSites,
  useSitesMultiselect,
} from "hooks";

import { getUserRole, USER_ROLES } from "recoils";
import { CSVLink } from "react-csv";
import { EsgExcelExport } from "assets";

import {
  sendInviteUser,
  deleteUser,
  deleteUserSite,
  getUsers,
  updateUserRole,
  updateUserSite,
  UserDTO,
} from "api";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";
import { QUERY_KEYS, STATUS_CODES } from "consts";

import { InviteUsersForm } from "./invite-users-form";
import { USER_ROLE, USER_ROLE_APPROVAL_FLOW_OPTIONS } from "./consts";
import { getManageUserColumns } from "./manage-users-columns";

export const InvitedUsersList: FunctionComponent = () => {
  const {
    mutateAsync: fetchUser,
    data: users,
    isLoading: areUsersLoading,
  } = useMutation(getUsers);

  const [dataEntriesStatus, setDataEntriesStatus] = useState<any>([]);

  const { data: sites } = useSites();
  const { data: configData } = useGetConfigParameter();
  const queryClient = useQueryClient();
  const { success: notifySuccess, error: notifyError } = useNotify();
  const notify = useMemo(
    () => ({ success: notifySuccess, error: notifyError }),
    [notifySuccess, notifyError],
  );
  const { trackEvent } = useAmplitude();
  const { isSitesLoading, sitesOptions } = useSitesMultiselect({
    withQueryParams: false,
  });

  const { mutateAsync: deleteUsers } = useMutation(deleteUser);
  const { mutateAsync: sendInviteUsersRequest } = useMutation(sendInviteUser);
  const { mutateAsync: updateRole } = useMutation(updateUserRole);
  const { mutateAsync: updateUserSiteSetting } = useMutation({
    mutationFn: ({ userId, siteId }: { userId: number; siteId: number }) =>
      updateUserSite(userId, siteId),
  });
  const { mutateAsync: deleteUserSiteSetting } = useMutation({
    mutationFn: ({ userId, siteId }: { userId: number; siteId: number }) =>
      deleteUserSite(userId, siteId),
  });

  const [loggedUser, setLoggedUser] = useState<UserDTO>();
  const [roleSelectOverride, setRoleSelectOverride] = useState<
    Record<number, string | null>
  >({});
  const [adminDropdownSelected, setAdminDropdownSelected] = useState<
    Record<number, boolean>
  >({});
  const [currentSitesByUserId, setCurrentSitesByUserId] = useState<
    Record<number, any[]>
  >({});
  const [previousSitesByUserId, setPreviousSitesByUserId] = useState<
    Record<number, any[]>
  >({});

  const secondApproval = configData?.find(
    (item: any) => item?.name === "second_layer_approval_feature",
  )?.configValue;

  const isSuperAdminViewer = useMemo(
    () => Boolean(loggedUser?.roles?.includes(USER_ROLES.SUPER_ADMIN)),
    [loggedUser],
  );

  const sitesOptionsForAdminUsers = useMemo(
    () => sitesOptions.filter((site) => site.value),
    [sitesOptions],
  );

  const titleFilterOptions = useMemo(() => {
    if (secondApproval === "false") return USER_ROLE;
    return [...USER_ROLE, ...USER_ROLE_APPROVAL_FLOW_OPTIONS].sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [secondApproval]);

  const siteIdToName = useMemo(() => {
    if (!sites?.length) return {} as Record<number, string>;
    return sites.reduce<Record<number, string>>((acc, site: any) => {
      if (site?.id != null) {
        acc[site.id] = site.name;
      }
      return acc;
    }, {});
  }, [sites]);

  const csvEmissionSummary = useMemo(() => {
    if (!users?.length) return [];
    return users.map((item: any) => ({
      "First Name": item?.firstName,
      "Last Name": item?.lastName,
      "User Email": item?.email,
      "Is User Active": item?.enabled === true ? "Yes" : "No",
      "User Role": getUserRole(item?.roles),
      "Data Provider Site": item?.sites
        ?.map((id: number) => siteIdToName[id])
        .filter(Boolean),
    }));
  }, [users, siteIdToName]);

  const activeUsers = useMemo(
    () => users?.filter((user) => user.enabled) ?? [],
    [users],
  );

  useEffect(() => {
    if (!sitesOptions.length || !users?.length) return;
    const enabledUsers = users.filter((user) => user.enabled);
    const computed: Record<number, any[]> = {};
    const getSelection = (user: UserDTO) => {
      let cached = computed[user.id];
      if (cached === undefined) {
        cached = sitesOptions.filter((site) =>
          user.sites?.includes(site.value as number),
        );
        computed[user.id] = cached;
      }
      return cached;
    };

    setCurrentSitesByUserId((prev) => {
      let changed = false;
      const next = { ...prev };

      enabledUsers.forEach((user) => {
        if (!(user.id in next)) {
          next[user.id] = getSelection(user);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
    setPreviousSitesByUserId((prev) => {
      let changed = false;
      const next = { ...prev };

      enabledUsers.forEach((user) => {
        if (!(user.id in next)) {
          next[user.id] = getSelection(user);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [users, sitesOptions]);

  const handleUpdateUserSite = useCallback(
    async (siteId: number, user: UserDTO) => {
      const { id: userId, email: userEmail } = user;
      try {
        await updateUserSiteSetting({ userId, siteId });
        notify.success(`Site update successfully for ${userEmail}!`);
      } catch (error) {
        console.error("Failed to update user site:", error);
      }
    },
    [notify, updateUserSiteSetting],
  );

  const handleDeleteUserSite = useCallback(
    async (siteId: number, user: UserDTO) => {
      const { id: userId, email: userEmail } = user;
      try {
        await deleteUserSiteSetting({ userId, siteId });
        notify.success(`Site update successfully for ${userEmail}!`);
      } catch (error) {
        console.error("Failed to delete user site:", error);
      }
    },
    [notify, deleteUserSiteSetting],
  );

  const onUpdateUser = useCallback(
    (user: any) => {
      const body = {
        id: user?.id,
        roles: user?.roles,
        siteIds: user?.siteIds,
      };

      updateRole(body, {
        onError: () =>
          notify.error(
            `Some error has happen while updating user ${user.email}!`,
          ),
        onSuccess: () => {
          notify.success(`User successfully updated ${user.email}!`);
          queryClient.invalidateQueries([QUERY_KEYS.USERS]);
        },
      });
    },
    [notify, queryClient, updateRole],
  );

  const onDeleteUser = useCallback(
    (user: UserDTO) => {
      const { email: userEmail } = user;
      deleteUsers(user.id, {
        onError: () =>
          notify.error(
            `Some error has happen while deleting user ${userEmail}!`,
          ),
        onSuccess: () => {
          notify.success(`User successfully deleted ${userEmail}!`);
          queryClient.invalidateQueries([QUERY_KEYS.USERS]);
        },
      });
    },
    [deleteUsers, notify, queryClient],
  );

  const disableDelete = useCallback(
    (user: UserDTO) => {
      if (loggedUser !== undefined && user.id === loggedUser.id) {
        return true;
      }
      return false;
    },
    [loggedUser],
  );

  const handleSitesSelectionChange = useCallback(
    (user: UserDTO, newSelectedValues: any[]) => {
      const previousSelectedValues = previousSitesByUserId[user.id] ?? [];

      setCurrentSitesByUserId((prev) => ({
        ...prev,
        [user.id]: newSelectedValues,
      }));

      if (previousSelectedValues.length > 0 && newSelectedValues.length === 0) {
        previousSelectedValues.forEach((value: any) => {
          handleDeleteUserSite(value?.value, user);
        });
        setPreviousSitesByUserId((prev) => ({ ...prev, [user.id]: [] }));
        return;
      }

      if (previousSelectedValues.length < newSelectedValues.length) {
        const addedValues = newSelectedValues.filter(
          (val: any) =>
            !previousSelectedValues.some(
              (prev: any) => prev.value === val.value,
            ),
        );
        addedValues.forEach((value: any) => {
          handleUpdateUserSite(value?.value, user);
        });
      } else {
        const removedValues = previousSelectedValues.filter(
          (prev: any) =>
            !newSelectedValues.some((val: any) => val.value === prev.value),
        );
        removedValues.forEach((value: any) => {
          handleDeleteUserSite(value?.value, user);
        });
      }

      setPreviousSitesByUserId((prev) => ({
        ...prev,
        [user.id]: newSelectedValues,
      }));
    },
    [previousSitesByUserId, handleUpdateUserSite, handleDeleteUserSite],
  );

  const handleRoleAutocompleteChange = useCallback(
    (user: UserDTO, role: string | null) => {
      setRoleSelectOverride((prev) => ({ ...prev, [user.id]: role }));
      setAdminDropdownSelected((prev) => ({
        ...prev,
        [user.id]: role === "Module Admin" || role === "Super Admin",
      }));
    },
    [],
  );

  const getRoleSelectValue = useCallback(
    (user: UserDTO) => roleSelectOverride[user.id] ?? null,
    [roleSelectOverride],
  );

  const handleSendInvitation = useCallback(
    async (user: UserDTO) => {
      const { id: userId, email } = user;

      try {
        await sendInviteUsersRequest(userId, {
          onSuccess() {
            notify.success(`Invitation successfully sent ${email}!`);
          }
        });

        const rolesCsv = dataEntriesStatus
          .map((t: any) => t?.label?.toLowerCase())
          .join(", ");
        await fetchUser(rolesCsv);

      } catch (error: unknown) {
        const axiosErr = error as {
          response?: { status?: number };
          config?: { data?: string };
        };

        if (axiosErr?.response?.status === STATUS_CODES.BAD_REQUEST) {
          notify.error(`${email} is already invited!`);
        } else {
          notify.error(
            `Some error has happen while sending invitation to ${email}!`,
          );
        }
      } finally {
        trackEvent("Invited User", "Manage Users");
        queryClient.invalidateQueries([QUERY_KEYS.USERS]);
      }
    },
    [
      sendInviteUsersRequest,
      notify,
      dataEntriesStatus,
      fetchUser,
      queryClient,
      trackEvent,
    ],
  );

  const manageUserColumns = useMemo(
    () =>
      getManageUserColumns({
        isApprovalFlowActive: secondApproval,
        isSitesLoading,
        sitesOptions,
        sitesOptionsForAdminUsers,
        isSuperAdminViewer,
        getCurrentSitesForUser: (user) => currentSitesByUserId[user.id] ?? [],
        onSitesSelectionChange: handleSitesSelectionChange,
        getRoleSelectValue,
        getIsAdminDropdownSelected: (user) =>
          adminDropdownSelected[user.id] ?? false,
        onRoleAutocompleteChange: handleRoleAutocompleteChange,
        onUpdateUser,
        onDeleteUser,
        disableDelete,
        notify,
        onSendInvitation: handleSendInvitation,
      }),
    [
      secondApproval,
      isSitesLoading,
      sitesOptions,
      sitesOptionsForAdminUsers,
      isSuperAdminViewer,
      currentSitesByUserId,
      adminDropdownSelected,
      handleSitesSelectionChange,
      getRoleSelectValue,
      handleRoleAutocompleteChange,
      onUpdateUser,
      onDeleteUser,
      disableDelete,
      notify,
      handleSendInvitation,
    ],
  );

  useEffect(() => {
    const rolesCsv = dataEntriesStatus
      .map((t: any) => t?.label?.toLowerCase())
      .join(", ");
    fetchUser(rolesCsv);
  }, [dataEntriesStatus, fetchUser]);

  useEffect(() => {
    const stored = sessionStorage.getItem("user");
    if (stored) {
      setLoggedUser(JSON.parse(stored));
    }
  }, []);

  return (
    <>
      <InviteUsersForm />
      <Divider sx={{ width: "100%" }} color="#9C9C9C" />
      <Box
        sx={{ display: "flex", justifyContent: "space-between", px: "24px" }}
      >
        <Stack alignItems="center" direction="row" spacing={1}>
          {USER_ROLE && (
            <Box sx={{ minWidth: "240px", width: "fit-content" }}>
              <EsgMultiselectControlledField
                label="View by Title"
                selectedValues={dataEntriesStatus}
                setSelectedValues={setDataEntriesStatus}
                options={titleFilterOptions}
                isAllSelectedOnEmptySelection
              />
            </Box>
          )}
        </Stack>
        {users && (
          <CSVLink
            style={{ textDecoration: "none" }}
            data={csvEmissionSummary}
            filename="User-List-Data.csv"
          >
            <Button
              sx={{
                height: "36px",
                marginLeft: "3px",
                borderRadius: "8px",
                fontSize: "14px",
                fontFamily: "Inter",
                mt: "18px",
                width: "196px",
              }}
              type="submit"
              startIcon={<EsgExcelExport />}
              variant="contained"
            >
              <Label14Medium>Export User List</Label14Medium>
            </Button>
          </CSVLink>
        )}
      </Box>
      <Divider sx={{ width: "100%" }} />
      <Box
        sx={{
          height: "100%",
          borderBottom: `1px solid ${COLORS.extraLightGray}`,
          paddingBottom: 3,
          px: "24px",
        }}
      >
        <CustomMaterialTable
          data={activeUsers}
          columns={manageUserColumns}
          state={{ isLoading: areUsersLoading }}
          enablePagination={false}
          enableTopToolbar={false}
          enableColumnActions={false}
          enableRowVirtualization={false}
          customPlaceholder="There are no users with the selected role."
          headCellSx={{
            border: "none",
            borderBottom: "none",
            "&::after": { display: "none" },
          }}
          bodyCellSx={{
            border: "none",
            borderBottom: "none",
          }}
          bodyRowSx={{ "& .MuiTableCell-root": { borderBottom: "none" } }}
          muiTableContainerProps={{
            sx: {
              height: "calc(100vh - 330px)",
              width: "calc(100vw - 150px)",
              overflowY: "scroll",
              "& .MuiTable-root": { borderCollapse: "collapse" },
              "& .MuiTableCell-root": {
                border: "none",
                borderBottom: "none !important",
                borderRight: "none !important",
              },
              "& tbody tr td": {
                paddingY: "10px",
              },
              "& thead tr th": {
                paddingY: "12px",
              },
              "& thead tr th:first-of-type > div": { pl: "8px" },
            },
          }}
        />
      </Box>
    </>
  );
};
